import { resolveBackgroundColorValue } from "utils/styleUtils";

export const DEFAULT_LOGIN_LOGO_PATH = "/assets/images/149MKM.png";
export const DEFAULT_HEADER_LOGO_PATH = "/assets/images/149MKM_trans_blue.png";
export const DEFAULT_LOGIN_BACKGROUND_COLOR = "#FFF";
export const DEFAULT_HEADER_BACKGROUND_COLOR = "#ffffff";
export const DEFAULT_SIDEBAR_BACKGROUND_COLOR = "#F1F2F5";
export const DEFAULT_COMPANY_NAME = "Compliance";

const BRANDING_ASSET_API_PATH_PREFIX = "/auth/ui-config/";
const API_PROXY_PATH_PREFIX = "/api";
const bundledAssetVersion = Date.now();

const isOfflineMode = () => process.env.REACT_APP_MODE_OFFLINE === "true";
const withCacheBust = (path) => `${path}?ts=${bundledAssetVersion}`;

const trimString = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    const trimmed = trimString(value);
    if (trimmed) return trimmed;
  }
  return "";
};

const getPayloadField = (payload, field) => {
  if (isOfflineMode() || !payload || typeof payload !== "object") return undefined;
  return payload[field];
};

const env = {
  loginLogoUrl: () => process.env.REACT_APP_LOGIN_LOGO_URL,
  loginBackgroundColor: () => process.env.REACT_APP_LOGIN_BACKGROUND,
  faviconUrl: () => process.env.REACT_APP_FAVICON_URL,
  appName: () => process.env.REACT_APP_NAME,
  companyName: () => process.env.REACT_APP_API_COMPANY_NAME,
  headerLogoUrl: () => process.env.REACT_APP_HEADER_LOGO_URL,
  headerBrandName: () => process.env.REACT_APP_HEADER_BRAND_NAME,
  headerBackgroundColor: () => process.env.REACT_APP_HEADER_BACKGROUND_COLOR,
  sidebarLogoUrl: () => process.env.REACT_APP_SIDEBAR_LOGO_URL,
  sidebarBackgroundColor: () => process.env.REACT_APP_SIDEBAR_BACKGROUND_COLOR,
  brandName: () => process.env.REACT_APP_BRAND_NAME,
};

/**
 * Logo URLs from the backend are rooted at `/auth/...`, while browser API
 * traffic is routed through the frontend's `/api` reverse-proxy path.
 * Absolute URLs, env asset paths, and already-proxied URLs are unchanged.
 */
export function resolveBrandingAssetUrl(value) {
  const url = trimString(value);
  if (!url) return "";
  if (url.startsWith(BRANDING_ASSET_API_PATH_PREFIX)) {
    return `${API_PROXY_PATH_PREFIX}${url}`;
  }
  return url;
}

export function resolveLoginLogoUrl(payload = null) {
  if (isOfflineMode()) return withCacheBust(DEFAULT_LOGIN_LOGO_PATH);

  const configuredUrl = firstNonEmpty(
    getPayloadField(payload, "login_logo_url"),
    env.loginLogoUrl()
  );
  return resolveBrandingAssetUrl(configuredUrl) || withCacheBust(DEFAULT_LOGIN_LOGO_PATH);
}

export function resolveLoginBackgroundColor(payload = null) {
  return resolveBackgroundColorValue(
    firstNonEmpty(
      getPayloadField(payload, "background_color"),
      env.loginBackgroundColor()
    ),
    DEFAULT_LOGIN_BACKGROUND_COLOR
  );
}

export function resolvePreLoginBranding(payload = null) {
  const appName = firstNonEmpty(env.appName(), DEFAULT_COMPANY_NAME);
  const companyName = firstNonEmpty(
    env.companyName(),
    env.appName(),
    DEFAULT_COMPANY_NAME
  );

  return {
    loginLogoUrl: resolveLoginLogoUrl(payload),
    loginBackgroundColor: resolveLoginBackgroundColor(payload),
    faviconUrl: firstNonEmpty(env.faviconUrl()),
    appName,
    companyName,
  };
}

export function resolveHeaderBranding(payload = null) {
  const text = firstNonEmpty(
    getPayloadField(payload, "header_text"),
    env.headerBrandName()
  );

  if (isOfflineMode()) {
    if (text) return { logoUrl: "", text };
    return { logoUrl: withCacheBust(DEFAULT_HEADER_LOGO_PATH), text: "" };
  }

  const logoUrl = resolveBrandingAssetUrl(firstNonEmpty(
    getPayloadField(payload, "header_logo_url"),
    env.headerLogoUrl()
  ));

  return {
    logoUrl,
    text: text || "",
  };
}

export function resolveHeaderBackgroundColor(payload = null) {
  return resolveBackgroundColorValue(
    firstNonEmpty(
      getPayloadField(payload, "header_background_color"),
      env.headerBackgroundColor()
    ),
    DEFAULT_HEADER_BACKGROUND_COLOR
  );
}

export function resolveHeaderLogoUrl(payload = null) {
  if (isOfflineMode()) return withCacheBust(DEFAULT_HEADER_LOGO_PATH);

  return resolveBrandingAssetUrl(firstNonEmpty(
    getPayloadField(payload, "header_logo_url"),
    env.headerLogoUrl()
  ));
}

export function resolveSidebarBranding(payload = null) {
  if (isOfflineMode()) {
    return { type: "logo", value: withCacheBust(DEFAULT_HEADER_LOGO_PATH) };
  }

  const logoUrl = resolveBrandingAssetUrl(firstNonEmpty(
    getPayloadField(payload, "sidebar_logo_url"),
    env.sidebarLogoUrl()
  ));
  if (logoUrl) return { type: "logo", value: logoUrl };

  return null;
}

export function resolveSidebarBackgroundColor(payload = null) {
  return resolveBackgroundColorValue(
    firstNonEmpty(
      getPayloadField(payload, "sidebar_background_color"),
      env.sidebarBackgroundColor()
    ),
    DEFAULT_SIDEBAR_BACKGROUND_COLOR
  );
}

export function resolvePostLoginBranding(payload = null) {
  const headerBranding = resolveHeaderBranding(payload);
  const sidebarBranding = resolveSidebarBranding(payload);
  const headerBackgroundColor = resolveHeaderBackgroundColor(payload);
  const sidebarBackgroundColor = resolveSidebarBackgroundColor(payload);
  const brandName = firstNonEmpty(
    getPayloadField(payload, "header_text"),
    env.brandName(),
    env.headerBrandName()
  );
  const companyName = firstNonEmpty(
    env.companyName(),
    env.appName(),
    DEFAULT_COMPANY_NAME
  );

  return {
    headerBranding,
    headerLogoUrl: resolveHeaderLogoUrl(payload),
    headerBackgroundColor,
    sidebarBranding,
    sidebarBackgroundColor,
    brandName,
    companyName,
    faviconUrl: firstNonEmpty(env.faviconUrl()),
    appName: firstNonEmpty(env.appName(), DEFAULT_COMPANY_NAME),
  };
}

export function resolveDocumentBranding() {
  return {
    title: firstNonEmpty(env.appName(), DEFAULT_COMPANY_NAME),
    faviconUrl: firstNonEmpty(env.faviconUrl()),
  };
}
